import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MusicVideoClipSource, MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { useRealtimePollInterval } from '../../realtime/useRealtimePollInterval';
import { useGetFootageQuery } from '../musicVideoEditorApi';
import { bankFootageView, footageKey, ownFootageView, type FootageView } from './footageView';
import { useBankCatalog, type BankCatalogOptions } from './useBankCatalog';

/** The safety read of the own clips while an AI clip or an upload works and the socket is down. */
export const FOOTAGE_POLL_MS = 5000;
/** The video links of the footage answers end after one hour: the editor reads them again before. */
export const FOOTAGE_LINK_REFRESH_MS = 45 * 60 * 1000;
/** A media error reads the links again at most this often (a broken file must not loop the reads). */
export const FOOTAGE_LINK_ERROR_GAP_MS = 30 * 1000;

/** True when a media error may read the links again: the last read is `FOOTAGE_LINK_ERROR_GAP_MS` ago. Pure. */
export function mayRefreshLinks(lastRefreshAt: number, now: number): boolean {
  return now - lastRefreshAt >= FOOTAGE_LINK_ERROR_GAP_MS;
}

export interface FootageCatalog {
  /** The clips of the account, this song first (Yours, AI). */
  own: FootageView[];
  /** The bank clips of the cut read so far. */
  bank: FootageView[];
  /** The clip of a source, or null when it is not known (yet). */
  lookup: (source: MusicVideoClipSource) => FootageView | null;
  isOwnLoading: boolean;
  ownError: ErrorAPIResult['error'] | undefined;
  retryOwn: () => void;
  /**
   * Reads the video links again (they end after one hour): call it on a `<video>` media error. The
   * catalog also reads them every `FOOTAGE_LINK_REFRESH_MS`.
   */
  refreshLinks: () => void;
}

/**
 * All the footage the editor knows: the own clips (`GET /footage?catalogItemUuid`) and the bank clips
 * of the cut (`useBankCatalog`, one `uuids` query). The realtime event `footage.clip.updated` reads the own clips again; the list
 * polls only while a clip works and the socket is down. The video links end after one hour, so
 * both lists are read again every 45 minutes and after a media error of the preview.
 *
 * Showcase mode has no account: no own clips, no poll, and the bank clips come from the public
 * lookup of the showcase (`options.seed` holds the clips of the showcase cut).
 */
export function useFootageCatalog(
  accountId: number,
  catalogItemUuid: string,
  doc: Pick<MusicVideoDoc, 'clips'>,
  options: BankCatalogOptions = {}
): FootageCatalog {
  const isShowcase = options.mode === 'showcase';
  const bank = useBankCatalog(accountId, doc, options);
  const [isWorking, setIsWorking] = useState(false);
  const pollingInterval = useRealtimePollInterval(isWorking ? FOOTAGE_POLL_MS : 0);
  const ownQuery = useGetFootageQuery({ accountId, catalogItemUuid }, { pollingInterval, skip: isShowcase });
  const hasWorking = useMemo(
    () => ownQuery.data?.clips.some((clip) => clip.status === 'queued' || clip.status === 'generating') === true,
    [ownQuery.data]
  );
  useEffect(() => setIsWorking(hasWorking), [hasWorking]);

  const own = useMemo(
    () => (ownQuery.data?.clips ?? []).filter((clip) => !clip.archived).map(ownFootageView),
    [ownQuery.data]
  );
  const bankViews = useMemo(() => bank.items.map(bankFootageView), [bank.items]);
  const byKey = useMemo(() => {
    const map = new Map<string, FootageView>();
    bankViews.forEach((view) => map.set(view.key, view));
    // An archived clip still plays in a cut that uses it, until the next make (spec §4.3).
    (ownQuery.data?.clips ?? []).forEach((clip) => {
      const view = ownFootageView(clip);
      map.set(view.key, view);
    });
    return map;
  }, [bankViews, ownQuery.data]);
  const lookup = useCallback((source: MusicVideoClipSource) => byKey.get(footageKey(source)) ?? null, [byKey]);
  const { refetch: refetchOwn } = ownQuery;
  // A skipped query cannot be read again: the showcase has no own clips.
  const refetch = useCallback(
    () => (isShowcase ? Promise.resolve() : refetchOwn().then(() => undefined)),
    [isShowcase, refetchOwn]
  );
  const retryOwn = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const { refresh: refreshBank } = bank;
  const lastRefreshAt = useRef(Date.now());
  const readLinksAgain = useCallback(() => {
    lastRefreshAt.current = Date.now();
    refreshBank();
    refetch().catch(console.error);
  }, [refetch, refreshBank]);
  const refreshLinks = useCallback(() => {
    if (mayRefreshLinks(lastRefreshAt.current, Date.now())) {
      readLinksAgain();
    }
  }, [readLinksAgain]);
  useEffect(() => {
    const timer = window.setInterval(readLinksAgain, FOOTAGE_LINK_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [readLinksAgain]);

  return {
    own,
    bank: bankViews,
    lookup,
    isOwnLoading: ownQuery.isLoading,
    ownError: ownQuery.error,
    retryOwn,
    refreshLinks,
  };
}
